import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, imgUrl } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { toast } from "sonner";
import { Upload, X, ImagePlus, Loader2 } from "lucide-react";

export default function HostelImages({ hostel, open, onClose, onChange }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(""); // e.g. "Compressing 3/5…"
  const [images, setImages] = useState(hostel?.images || []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setProgress("");
    try {
      let done = 0;
      for (const raw of files) {
        done++;
        setProgress(`Optimising ${done}/${files.length}…`);
        // Downscale huge phone photos before hitting the network
        const f = await compressImage(raw);
        const savedKB = raw.size > f.size ? Math.round((raw.size - f.size) / 1024) : 0;
        const fd = new FormData();
        fd.append("file", f);
        setProgress(`Uploading ${done}/${files.length}…`);
        const { data } = await api.post(`/hostels/${hostel.id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.url]);
        if (savedKB > 100) console.info(`[compress] ${raw.name}: saved ${savedKB} KB`);
      }
      toast.success(`${files.length} photo(s) uploaded`);
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress("");
      if (ref.current) ref.current.value = ""; // let same-file re-pick fire onChange
    }
  };

  const remove = async (url) => {
    try {
      await api.delete(`/hostels/${hostel.id}/images`, { params: { url } });
      setImages((prev) => prev.filter((u) => u !== url));
      onChange?.();
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">Photos · {hostel?.name}</DialogTitle></DialogHeader>
        <div>
          <button type="button" data-testid="hostel-image-upload"
            onClick={() => ref.current?.click()}
            disabled={uploading}
            className="w-full border-2 border-dashed border-slate-300 hover:border-orange-600 rounded-2xl p-8 flex flex-col items-center justify-center gap-3 transition-colors bg-slate-50">
            <input ref={ref} hidden multiple accept="image/*,.heic,.heif" type="file" onChange={upload} />
            {uploading ? <Loader2 className="animate-spin text-orange-800" size={28} /> : <ImagePlus size={28} className="text-orange-800" />}
            <div className="font-semibold">{uploading ? (progress || "Uploading…") : "Click to upload photos"}</div>
            <div className="text-xs text-slate-500">JPG · PNG · WEBP · HEIC (Samsung/iPhone) · Auto-shrunk to save data · multiple allowed</div>
          </button>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {images.map((u) => (
              <div key={u} className="relative aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 group">
                <img src={imgUrl(u)} alt="" className="w-full h-full object-cover" />
                <Button
                  data-testid="hostel-image-remove"
                  size="icon" variant="destructive"
                  onClick={() => remove(u)}
                  className="absolute top-2 right-2 h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <X size={14} />
                </Button>
              </div>
            ))}
            {images.length === 0 && (
              <div className="col-span-full text-center text-sm text-slate-500 py-6">No photos yet — the first uploaded photo becomes the cover.</div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
